import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * The playground's panel and mouse controls (`?play=1`, dev builds; lib/map/playground.js). Small on purpose
 * (Sam, 2026-10-19).
 *
 *   W A S D / arrows  walk, relative to the screen (Shift: twice as fast)     Q / E (hold)  turn your compass
 *   Right-click without dragging  a menu: add a friend there, or remove the friend under it
 *   Drag a friend  move them      Right-drag  pans the map as always - it never adds anyone
 */
const HIT_PX = 24;
const CLICK_PX = 5;

export default function MapPlayground({ play }) {
  useSyncExternalStore(play.subscribe, () => play.version);
  const [menu, setMenu] = useState(null); // { x, y, ll, friendId, name }
  // Folded on a phone-size screen, where it would cover a third of the map.
  const [open, setOpen] = useState(() => typeof window === 'undefined' || window.innerWidth > 520);

  useEffect(() => {
    const scene = () => window.__priiScene;
    play.setViewBearing(() => scene()?.viewBearing?.() ?? null);
    const onCanvas = (e) => e.target?.tagName === 'CANVAS';
    const friendAt = (e) => {
      const api = scene();
      if (!api?._people) return null;
      const r = e.target.getBoundingClientRect();
      const mx = e.clientX - r.left,
        my = e.clientY - r.top;
      let best = null,
        bestD = HIT_PX;
      for (const p of api._people.positions()) {
        if (p.self || !p.id) continue;
        const s = api.project(p.x, p.y, p.z);
        const d = Math.hypot(s.x - mx, s.y - my);
        if (d < bestD) {
          bestD = d;
          best = p.id;
        }
      }
      return best;
    };
    const llAt = (e) => {
      const ll = scene()?.llAt?.(e.clientX, e.clientY);
      return ll ? { lat: ll[0], lng: ll[1] } : null;
    };

    // Left: a press on a friend becomes a drag once it moves CLICK_PX; until then the map behaves as usual, so a plain
    // click still selects them. Right: the menu opens on RELEASE, and only if the mouse did not move - a right-drag is
    // the map's pan (macOS fires contextmenu on press, so that event cannot tell the two apart).
    let press = null,
      dragging = null,
      right = null;
    const down = (e) => {
      if (!onCanvas(e)) return;
      if (e.button === 2) {
        right = { x: e.clientX, y: e.clientY };
        return;
      }
      if (e.button !== 0) return;
      setMenu(null);
      const id = friendAt(e);
      press = id ? { id, x: e.clientX, y: e.clientY } : null;
    };
    const move = (e) => {
      if (right && Math.hypot(e.clientX - right.x, e.clientY - right.y) >= CLICK_PX) right = null;
      if (!press) return;
      if (!dragging && Math.hypot(e.clientX - press.x, e.clientY - press.y) < CLICK_PX) return;
      dragging = press.id;
      e.stopPropagation();
      const ll = llAt(e);
      if (ll) play.moveFriend(dragging, ll);
    };
    const up = (e) => {
      press = null;
      dragging = null;
      if (e.button !== 2 || !right || !onCanvas(e)) {
        right = null;
        return;
      }
      right = null;
      const friendId = friendAt(e);
      const ll = llAt(e);
      if (!friendId && !ll) return;
      setMenu({
        x: e.clientX,
        y: e.clientY,
        ll,
        friendId,
        name: friendId ? play.friends.find((f) => f.id === friendId)?.name : null,
      });
    };
    const context = (e) => {
      if (onCanvas(e)) e.preventDefault();
    };
    const key = (e) => {
      if (e.key === 'Escape') setMenu(null);
    };
    window.addEventListener('mousedown', down, true);
    window.addEventListener('mousemove', move, true);
    window.addEventListener('mouseup', up, true);
    window.addEventListener('contextmenu', context, true);
    window.addEventListener('keydown', key);
    return () => {
      play.setViewBearing(null);
      window.removeEventListener('mousedown', down, true);
      window.removeEventListener('mousemove', move, true);
      window.removeEventListener('mouseup', up, true);
      window.removeEventListener('contextmenu', context, true);
      window.removeEventListener('keydown', key);
    };
  }, [play]);

  const { speedMps, selfAccuracyM, friendAccuracyM } = play.settings;
  const n = play.friends.length;
  const accLabel = (m) => (m < 10 ? 'Off' : `${m} m`);
  return (
    <>
      <div className="map-play" role="region" aria-label="Map playground">
        <button
          type="button"
          className="map-play__title"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          Playground <span aria-hidden="true">{open ? '−' : '+'}</span>
        </button>
        {open && (
          <>
            <p className="map-play__hint">
              WASD / arrows walk (screen-relative) · Shift faster · hold Q / E to turn
            </p>
            <p className="map-play__hint">
              Right-click: add or remove a friend · drag a friend to move them
            </p>

            <label className="map-play__row" htmlFor="play-speed">
              <span>Walking speed</span>
              <b>{speedMps.toFixed(1)} m/s</b>
            </label>
            <input
              id="play-speed"
              type="range"
              min="0.5"
              max="6"
              step="0.1"
              value={speedMps}
              onChange={(e) => play.setSpeed(Number(e.target.value))}
            />

            <label className="map-play__row" htmlFor="play-acc-self">
              <span>Your accuracy</span>
              <b>{accLabel(selfAccuracyM)}</b>
            </label>
            <input
              id="play-acc-self"
              type="range"
              min="5"
              max="60"
              value={selfAccuracyM}
              onChange={(e) => play.setSelfAccuracy(Number(e.target.value))}
            />
            <label className="map-play__row" htmlFor="play-acc-friends">
              <span>Friends' accuracy</span>
              <b>{accLabel(friendAccuracyM)}</b>
            </label>
            <input
              id="play-acc-friends"
              type="range"
              min="5"
              max="60"
              value={friendAccuracyM}
              onChange={(e) => play.setFriendAccuracy(Number(e.target.value))}
            />

            <div className="map-play__row">
              <span>Friends</span>
              <span className="map-play__stepper">
                <button
                  type="button"
                  onClick={() => play.setFriendCount(n - 1)}
                  disabled={n === 0}
                  aria-label="One fewer friend"
                >
                  −
                </button>
                <b>{n}</b>
                <button
                  type="button"
                  onClick={() => play.setFriendCount(n + 1)}
                  aria-label="One more friend"
                >
                  +
                </button>
              </span>
            </div>
            {n > 0 && (
              <button type="button" className="map-play__link" onClick={() => play.clearFriends()}>
                Remove all friends
              </button>
            )}
          </>
        )}
      </div>
      {menu && (
        <div className="map-play-menu" style={{ left: menu.x, top: menu.y }} role="menu">
          {menu.friendId ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                play.removeFriend(menu.friendId);
                setMenu(null);
              }}
            >
              Remove {menu.name ?? 'friend'}
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                play.addFriend(menu.ll);
                setMenu(null);
              }}
            >
              Add friend here
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="map-play-menu__cancel"
            onClick={() => setMenu(null)}
          >
            Cancel
          </button>
        </div>
      )}
    </>
  );
}
